import React, { useMemo, useState } from 'react';
import {
    Box,
    Text,
    Loader,
    Alert,
    Paper,
    Card,
    Group,
    Stack,
    Anchor,
    Table,
    ScrollArea,
    ActionIcon,
    TextInput,
} from '@mantine/core';
import { IconSearch, IconDownload, IconPrinter } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import SQLEditorPopup from '../../supply-chain/purchasing/SQLEditorPopup';

// Updated getColor with Mantine color system
const getColor = (color) => {
    const mapping = {
        green: '#51cf66',   // green.4
        yellow: '#ffd43b',  // yellow.4  
        red: '#ff6b6b',     // red.4
    };
    return color ? mapping[color.toLowerCase()] || 'transparent' : 'transparent';
};


const openPage = (url) => {
    window.open(url, '_blank');
};

const ColorLegend = () => (
    <Card withBorder radius="md" style={{ width: '100%' }}>
        <Text size="sm" fw={500} mb="xs">
            Legend
        </Text>
        <Stack gap="xs">
            {[
                { color: '#51cf66', label: 'Green: MTD ≥ 2, QTD ≥ 4, YTD ≥ 16' },
                { color: '#ffd43b', label: 'Yellow: MTD = 1, QTD 2-3, YTD 12-15' },
                { color: '#ff6b6b', label: 'Red: MTD = 0, QTD < 1, YTD < 11' },
            ].map(({ color, label }) => (
                <Group key={label} gap="xs" align="flex-start">
                    <Box
                        style={{
                            width: '16px',
                            height: '16px',
                            backgroundColor: color,
                            borderRadius: '2px',
                            flexShrink: 0,
                            marginTop: '2px',
                        }}
                    />
                    <Text size="xs" c="dimmed" style={{ flex: 1, lineHeight: 1.3 }}>
                        {label}
                    </Text>
                </Group>
            ))}
        </Stack>
    </Card>
);

const SalesDepartmentScorecardGrowthQtyDetails = () => {
    const navigate = useNavigate();

    // Add state for the SQL query popup and search
    const [openQueryPopup, setOpenQueryPopup] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Handler to close the popup
    const handleCloseQueryPopup = () => setOpenQueryPopup(false);

    // Fetch new business quantity details from your API endpoint
    const { data, isLoading, error } = useAuthenticatedData(
        `${config.apiBaseUrl}/sales/department/scorecard/new-business-qty-details`
    );

    // Process data and filter based on search
    const processedData = useMemo(() => {
        if (!data) return [];

        return data
            .map((record, index) => ({
                id: record.ccode || index,
                ...record,
            }))
            .filter(row =>
                !searchTerm ||
                row.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
    }, [data, searchTerm]);

    const formatNumber = (value) => {
        if (value == null) return '';
        return value.toFixed(0);
    };

    const handleCellClick = (field, ccode) => {
        if (['mtd', 'qtd', 'ytd'].includes(field)) {
            openPage(`/sales/salesperson/${ccode}/scorecard/new-business-growth-qty/${field}`);
        }
    };

    const exportToCSV = () => {
        if (!processedData.length) return;

        const headers = ['Salesperson', 'MTD', 'QTD', 'YTD'];
        const csvContent = [
            headers.join(','),
            ...processedData.map(row => [
                `"${row.name}"`,
                row.mtd || '',
                row.qtd || '',
                row.ytd || ''
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'growth-qty-details.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <Loader />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert color="red" title="Error">
                {error.message}
            </Alert>
        );
    }

    return (
        <Paper shadow="md" radius="md"  >
            <Stack gap="sm">
                {/* Title section */}
                <Box>
                    <Text
                        component="h1"
                        style={{
                            fontFamily: 'Orbitron, sans-serif',
                            color: '#FDB813',
                            fontStyle: 'italic',
                            fontSize: '1.5rem',
                            marginBottom: 0,
                            textAlign: 'left'
                        }}
                    >            Department New Business Qty Scorecard
                    </Text>

                    {/* Technical links */}
                    <Group gap="xs" mb="sm">
                        <Anchor
                            size="xs"
                            c="dimmed"
                            onClick={() =>
                                window.open(
                                    '/query-commander/editor?queryKey=sales-department-other-new-business-qty',
                                    '_blank'
                                )
                            }
                            style={{ cursor: 'pointer' }}
                        >
                            Other Query
                        </Anchor>
                    </Group>
                </Box>

                {/* Info cards container - tighter spacing */}
                <Group gap="xs" align="stretch" style={{ marginBottom: '0.5rem' }}>
                    <Box style={{ flex: 1, minWidth: '300px' }}>
                        <Card withBorder radius="md" style={{ height: '100%' }}>
                            <Text size="sm" fw={500} mb="xs">
                                About the Data
                            </Text>
                            <Text size="xs" c="dimmed">
                                These are the new business quantity details per salesperson. Each cell is colored based on performance indicators.
                            </Text>
                        </Card>
                    </Box>
                    <Box style={{ flex: 1, minWidth: '300px' }}>
                        <ColorLegend />
                    </Box>
                </Group>

                {/* Toolbar - reduced spacing */}
                <Group justify="space-between" mb="xs">
                    <TextInput
                        placeholder="Search salesperson..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        leftSection={<IconSearch size={16} />}
                        style={{ width: '300px' }}
                        size="sm"
                    />
                    <Group gap="xs">
                        <ActionIcon
                            variant="default"
                            onClick={exportToCSV}
                            title="Export to CSV"
                            size="sm"
                        >
                            <IconDownload size={16} />
                        </ActionIcon>
                        <ActionIcon
                            variant="default"
                            onClick={() => window.print()}
                            title="Print"
                            size="sm"
                        >
                            <IconPrinter size={16} />
                        </ActionIcon>
                    </Group>
                </Group>

                {/* Data Table - compact height */}
                <Paper withBorder style={{ height: '500px' }}>
                    <ScrollArea style={{ height: '100%' }}>
                        <Table striped highlightOnHover size="sm">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th style={{ width: '250px' }}>Salesperson</Table.Th>
                                    <Table.Th style={{ width: '150px' }}>MTD</Table.Th>
                                    <Table.Th style={{ width: '150px' }}>QTD</Table.Th>
                                    <Table.Th style={{ width: '150px' }}>YTD</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {processedData.map((row) => (
                                    <Table.Tr key={row.id}>
                                        <Table.Td>
                                            <Text size="sm" fw={500}>
                                                {row.name}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td
                                            style={{
                                                backgroundColor: getColor(row.mtdColor),
                                                cursor: 'pointer',
                                                fontWeight: 600,
                                            }}
                                            onClick={() => handleCellClick('mtd', row.ccode)}
                                        >
                                            <Text size="sm" fw={600} c="dark">
                                                {formatNumber(row.mtd)}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td
                                            style={{
                                                backgroundColor: getColor(row.qtdColor),
                                                cursor: 'pointer',
                                                fontWeight: 600,
                                            }}
                                            onClick={() => handleCellClick('qtd', row.ccode)}
                                        >
                                            <Text size="sm" fw={600} c="dark">
                                                {formatNumber(row.qtd)}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td
                                            style={{
                                                backgroundColor: getColor(row.ytdColor),
                                                cursor: 'pointer',
                                                fontWeight: 600,
                                            }}
                                            onClick={() => handleCellClick('ytd', row.ccode)}
                                        >
                                            <Text size="sm" fw={600} c="dark">
                                                {formatNumber(row.ytd)}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Paper>

                {/* Results count - minimal spacing */}
                <Text size="xs" c="dimmed">
                    Showing {processedData.length} result{processedData.length !== 1 ? 's' : ''}
                </Text>
            </Stack>

            {/* SQL Editor Popup */}
            <SQLEditorPopup
                open={openQueryPopup}
                onClose={handleCloseQueryPopup}
                title="Other New Business Qty Query"
                endpoint={`${config.apiBaseUrl}/sales/department/scorecard/queries/other-new-business-qty-summary`}
            />
        </Paper>
    );
};

export default SalesDepartmentScorecardGrowthQtyDetails;